import { Text } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { outlineTokens } from '@sixui/ui/themes/base/outline.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import { spacingTokens } from '@sixui/ui/themes/base/spacing.stylex';
import stylex from '@stylexjs/stylex';
import { round } from 'lodash';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { usePollQuery } from '~/lib/api/modules/Polls/hooks/usePollQuery';
import { usePollResultsQuery } from '~/lib/api/modules/Polls/hooks/usePollResultsQuery';

export type IPollStatsProps = IContainerProps & {
  appId: string;
  pollId: string;
};

const styles = stylex.create({
  results: {
    borderLeftWidth: outlineTokens.width$xs,
    borderLeftStyle: 'solid',
    borderLeftColor: colorSchemeTokens.outline,
    paddingTop: spacingTokens.padding$4,
    paddingBottom: spacingTokens.padding$4,
    display: 'flex',
    flexDirection: 'column',
    gap: spacingTokens.padding$2,
  },
  result: {
    height: spacingTokens.padding$8,
    color: colorSchemeTokens.onSurface,
    display: 'flex',
  },
  progression: (progression: number) => ({
    display: 'flex',
    alignItems: 'center',
    paddingLeft: spacingTokens.padding$2,
    backgroundColor: colorSchemeTokens.outlineVariant,
    borderTopRightRadius: shapeTokens.corner$xs,
    borderBottomRightRadius: shapeTokens.corner$xs,
    overflow: 'visible',
    whiteSpace: 'nowrap',
    width: `${progression}%`,
  }),
});

export const PollStats: React.FC<IPollStatsProps> = (props) => {
  const { sx, appId, pollId } = props;
  const { t } = useTranslation();
  const pollResultsQuery = usePollResultsQuery({
    appId,
    pollId,
  });
  const pollQuery = usePollQuery({
    appId,
    pollId,
  });
  const poll = pollQuery.data;

  const pollResults = pollResultsQuery.data;
  const totalCount = pollResults?.votes?.length ?? 0;
  const isEmpty = !pollResultsQuery.isLoading && !totalCount;

  return (
    <ContentBlock sx={sx}>
      {pollQuery.isError || pollResultsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={pollQuery.error ?? pollResultsQuery.error} />
        </ContentBlock.Body>
      ) : pollQuery.isLoading || pollResultsQuery.isLoading ? (
        <ContentBlock>
          <ContentBlock.Body first>
            {/* TODO: use skeletons */}
            {t('common.loading')}
          </ContentBlock.Body>
        </ContentBlock>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('PollStats.empty.title')} />
      ) : (
        <>
          <ContentBlock.Title>{poll?.title}</ContentBlock.Title>

          <ContentBlock.Body>
            <div {...stylex.props(styles.results)}>
              {pollResults?.poll?.options?.map((option) => (
                <div key={option.id} {...stylex.props(styles.result)}>
                  <Text
                    sx={styles.progression(
                      round(
                        ((pollResults?.counters?.[option.id] ?? 0) /
                          totalCount) *
                          100,
                        1,
                      ),
                    )}
                    variant="label"
                    size="md"
                  >
                    {t('PollStats.label', {
                      text: option.text,
                      progression: round(
                        ((pollResults?.counters?.[option.id] ?? 0) /
                          totalCount) *
                          100,
                        1,
                      ),
                      count: pollResults?.counters?.[option.id] ?? 0,
                    })}
                  </Text>
                </div>
              ))}
            </div>
          </ContentBlock.Body>
        </>
      )}
    </ContentBlock>
  );
};
